import "./style.css";
import { initFlutedGlass } from "./flutedGlassMount.jsx";

// Smooth scroll for anchor links
document.addEventListener("DOMContentLoaded", () => {
  // Handle smooth scrolling for navigation links
  const navLinks = document.querySelectorAll('a[href^="#"]');

  // Remove FAQ structured data if the section isn't rendered on this page
  const faqList = document.getElementById("faq-list");
  if (!faqList) {
    const faqStructuredData = document.getElementById("faq-structured-data");
    if (faqStructuredData?.parentElement) {
      faqStructuredData.parentElement.removeChild(faqStructuredData);
    }
  }

  navLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const targetId = link.getAttribute("href");

      if (targetId === "#") return;

      const targetElement = document.querySelector(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    });
  });

  // Scroll-driven video scrubbing
  const video = document.getElementById("scroll-video");

  if (video && "IntersectionObserver" in window) {
    let scrollListener = null;
    let animationFrameId = null;
    let isVideoInView = false;

    // Function to update video time smoothly
    const updateVideoTime = () => {
      if (!isVideoInView) return;

      const rect = video.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate progress: 0 when entering viewport, 1 when leaving
      const progress = Math.max(
        0,
        Math.min(1, (viewportHeight - rect.top) / (viewportHeight + rect.height))
      );

      // Update video time based on scroll progress
      if (video.duration) {
        video.currentTime = progress * video.duration;
      }

      animationFrameId = null;
    };

    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            isVideoInView = true;

            // Start tracking scroll when video is in viewport
            if (!scrollListener) {
              scrollListener = () => {
                // Use requestAnimationFrame to throttle updates and sync with browser paint
                if (!animationFrameId) {
                  animationFrameId = requestAnimationFrame(updateVideoTime);
                }
              };

              window.addEventListener("scroll", scrollListener, {
                passive: true,
              });
              scrollListener(); // Initial call
            }
          } else {
            isVideoInView = false;

            // Stop tracking when video leaves viewport
            if (scrollListener) {
              window.removeEventListener("scroll", scrollListener);
              scrollListener = null;
            }

            // Cancel any pending animation frame
            if (animationFrameId) {
              cancelAnimationFrame(animationFrameId);
              animationFrameId = null;
            }
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "0px",
      }
    );

    videoObserver.observe(video);
  }

  // Attestation expand/collapse toggle (mobile)
  const attestationToggle = document.getElementById("attestation-toggle");
  const attestationMore = document.getElementById("attestation-more");
  const attestationToggleText = document.getElementById("attestation-toggle-text");

  if (attestationToggle && attestationMore) {
    attestationToggle.addEventListener("click", () => {
      const isExpanded = window.getComputedStyle(attestationMore).display !== "none";

      if (isExpanded) {
        attestationMore.style.display = "none";
        attestationToggleText.textContent = "See more";
      } else {
        attestationMore.style.display = "flex";
        attestationToggleText.textContent = "See less";
      }
    });
  }

  // Calendly Popup Widget with UTM Parameter Tracking
  const initializeCalendlyIntegration = () => {
    // Check if localStorage is available
    function isLocalStorageAvailable() {
      try {
        const test = "__localStorage_test__";
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    }

    const localStorageAvailable = isLocalStorageAvailable();

    // Get UTM parameters from current URL
    function getUTMParameters() {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParams = {};

      searchParams.forEach((value, key) => {
        if (key.startsWith("utm_") && value.trim()) {
          utmParams[key] = value;
        }
      });

      return utmParams;
    }

    // Store parameters in localStorage for persistence across sessions
    function storeParameters(params) {
      if (!localStorageAvailable) return;

      try {
        for (const key in params) {
          if (
            Object.prototype.hasOwnProperty.call(params, key) &&
            (key.startsWith("utm_") || key === "referrer") &&
            params[key]
          ) {
            localStorage.setItem(key, params[key]);
          }
        }
      } catch (e) {
        console.error("Calendly: Error storing parameters:", e);
      }
    }

    // Retrieve stored parameters from localStorage
    function retrieveParameters() {
      if (!localStorageAvailable) return {};

      const params = {};
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith("utm_") || key === "referrer")) {
            const value = localStorage.getItem(key);
            if (value) {
              params[key] = value;
            }
          }
        }
      } catch (e) {
        console.error("Calendly: Error retrieving parameters:", e);
      }
      return params;
    }

    // Convert UTM params to Calendly API format (camelCase)
    function formatUTMForCalendly(params) {
      const calendlyUTM = {};

      const mapping = {
        utm_campaign: "utmCampaign",
        utm_source: "utmSource",
        utm_medium: "utmMedium",
        utm_content: "utmContent",
        utm_term: "utmTerm",
      };

      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key) && mapping[key]) {
          calendlyUTM[mapping[key]] = params[key];
        }
      }

      return calendlyUTM;
    }

    // Check if referrer is external (not from our own domain)
    function isExternalReferrer(referrer) {
      try {
        const referrerURL = new URL(referrer);
        const currentHostname = window.location.hostname;
        return referrerURL.hostname !== currentHostname;
      } catch (e) {
        return false;
      }
    }

    // Get and store referrer information (first-touch attribution)
    function getReferrer() {
      if (!localStorageAvailable) return;

      const referrer = document.referrer;
      if (referrer && isExternalReferrer(referrer)) {
        try {
          localStorage.setItem("referrer", referrer);
          if (!localStorage.getItem("utm_source")) {
            localStorage.setItem("utm_source", referrer);
          }
        } catch (e) {
          console.error("Calendly: Error storing referrer:", e);
        }
      }
    }

    // Extract search keywords from referrer URL
    function getSearchKeywords(referrer) {
      let keywords = "";
      try {
        const referrerURL = new URL(referrer);
        const hostname = referrerURL.hostname.toLowerCase();

        if (hostname.includes("google")) {
          keywords = referrerURL.searchParams.get("q");
        } else if (hostname.includes("bing")) {
          keywords = referrerURL.searchParams.get("q");
        } else if (hostname.includes("yahoo")) {
          keywords = referrerURL.searchParams.get("p");
        } else if (hostname.includes("duckduckgo")) {
          keywords = referrerURL.searchParams.get("q");
        } else if (hostname.includes("baidu")) {
          keywords = referrerURL.searchParams.get("wd");
        }
      } catch (e) {
        console.error("Calendly: Error parsing referrer URL:", e);
      }
      return keywords;
    }

    // Initialize UTM tracking on page load
    const referrer = document.referrer;

    // Store referrer on first visit (first-touch attribution)
    if (referrer && localStorageAvailable && !localStorage.getItem("referrer")) {
      getReferrer();
      const keywords = getSearchKeywords(referrer);
      if (keywords) {
        try {
          localStorage.setItem("search_keywords", keywords);
        } catch (e) {
          console.error("Calendly: Error storing search keywords:", e);
        }
      }
    }

    // Get UTM parameters from current URL
    let utmParams = getUTMParameters();

    // If new UTM parameters exist, store them
    if (Object.keys(utmParams).length > 0) {
      storeParameters(utmParams);
    } else {
      // Otherwise, retrieve stored parameters
      const storedParams = retrieveParameters();
      if (Object.keys(storedParams).length > 0) {
        utmParams = storedParams;
      }
    }

    // Setup Calendly popup links
    function setupCalendlyPopup() {
      const calendlyLinks = document.querySelectorAll('a[href*="calendly.com"]');

      calendlyLinks.forEach((link) => {
        // Update href with UTM parameters as fallback
        try {
          const url = new URL(link.href);
          if (url.hostname.includes("calendly.com")) {
            for (const key in utmParams) {
              if (
                Object.prototype.hasOwnProperty.call(utmParams, key) &&
                key.startsWith("utm_") &&
                utmParams[key]
              ) {
                url.searchParams.set(key, utmParams[key]);
              }
            }
            link.href = url.toString();
          }
        } catch (e) {
          console.error("Calendly: Error updating link href:", e);
        }

        // Add click event listener for popup widget
        link.addEventListener("click", function (e) {
          // Check if Calendly widget is loaded
          if (typeof Calendly !== "undefined" && Calendly.initPopupWidget) {
            e.preventDefault();

            // Get base URL without query params for popup widget
            const linkUrl = new URL(link.href);
            const baseUrl = `${linkUrl.origin}${linkUrl.pathname}`;

            // Prepare Calendly widget options per official API docs
            const options = {
              url: baseUrl,
            };

            // Add UTM parameters if they exist
            const formattedUTM = formatUTMForCalendly(utmParams);
            if (Object.keys(formattedUTM).length > 0) {
              options.utm = formattedUTM;
            }

            // Open Calendly popup
            try {
              Calendly.initPopupWidget(options);
            } catch (error) {
              console.error("Calendly: Error opening popup widget:", error);
              // Fallback: open in new tab (href already has UTM parameters)
              window.open(link.href, "_blank", "noopener,noreferrer");
            }
          }
          // If Calendly script not loaded, let default link behavior work
        });
      });
    }

    // Wait for Calendly script to load
    function waitForCalendly() {
      if (typeof Calendly !== "undefined") {
        setupCalendlyPopup();
      } else {
        // Check every 100ms for Calendly to load
        const checkInterval = setInterval(() => {
          if (typeof Calendly !== "undefined") {
            clearInterval(checkInterval);
            clearTimeout(timeoutId);
            setupCalendlyPopup();
          }
        }, 100);

        // Timeout after 10 seconds
        const timeoutId = setTimeout(() => {
          clearInterval(checkInterval);
          if (typeof Calendly === "undefined") {
            console.warn("Calendly: Widget script failed to load within 10 seconds");
          }
          // Setup links anyway for fallback behavior
          setupCalendlyPopup();
        }, 10000);
      }
    }

    waitForCalendly();
  };

  // Initialize Calendly integration
  initializeCalendlyIntegration();

  // Initialize FlutedGlass shader component
  initFlutedGlass();
});
